import { useState } from "react";
import { api, type Slot } from "../api.ts";
import { C, mono } from "../theme.ts";
import { Markdown } from "../components/Markdown.tsx";

// The footer line on Today: the rhythms, and whatever each slot is offering.
//
// ───────────────────────────────────────────────────────────────────────────
// Read this before changing anything in this file.
//
//     The system may know where you are.
//     It may never tell you how you're doing.
//
// A slot knows its position in a rotation. That position is never surfaced as
// a verdict, which in this file means all of the following are forbidden:
//
//   · a count of anything — items done, days, sessions, meals
//   · a streak, a percentage, a progress bar or a ring
//   · a date a slot was last done, or how long is left of a rest interval
//   · a history view, or any way to reach one
//   · a notification of any kind
//   · a colour, badge, weight or ordering that means *behind*
//
// A suppressed slot is absent from the array the API sends, not present and
// dimmed — so there is nothing here to grey out and nothing to strike through.
// The line does not distinguish a slot that is resting from one that does not
// exist, and it must not learn how: the API does not send the interval, which
// is what makes that unbuildable rather than merely undone.
//
// The whole interaction is: tap a label, read the detail, press done. Done
// moves the rotation on. Nothing is said about the doing of it — no toast, no
// tick, no "nice one". The line simply shows the next thing afterwards.
// ───────────────────────────────────────────────────────────────────────────

/**
 * The Training slot's two items are `Variant 1 · push and legs` and
 * `Variant 2 · pull and hips` (session 05's brief, as amended). An item whose
 * label names a variant gets one link into the Workout view, at that variant.
 * That is the whole integration: the footer says which variant is next, the
 * link reaches the movements. Nothing is read back the other way.
 *
 * Detected from the label rather than a stored field, because a slot item has
 * no field for it and adding one would be a column for a single link. If the
 * label ever stops saying "Variant n", the link simply does not appear.
 */
const variantOf = (label: string): 1 | 2 | null => {
  const m = /\bVariant\s*([12])\b/i.exec(label);
  return m ? (Number(m[1]) as 1 | 2) : null;
};

export function Footer({
  slots, onChanged, onOpenWorkout,
}: {
  slots: Slot[];
  onChanged: () => void;
  /** Open the Workout view at a variant. Absent where there is no Workout view to open. */
  onOpenWorkout?: (variant: 1 | 2) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const slot = slots.find((s) => s.id === open) ?? null;

  const done = async (id: string) => {
    setBusy(true); setError(null);
    try {
      await api.advanceSlot(id);
      // Closed without comment. The rotation has moved; the line will say so
      // by showing something else, which is the whole of the feedback.
      setOpen(null);
      onChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* One grey line, in the place the rhythms line used to share with
          this. Since session 17's Today rebuild the rhythms names live in
          the Classical footer above this one; this line is the slots only
          now, so it starts directly on the first slot label rather than a
          leading "rhythms" word that no longer introduces anything. */}
      {slots.length > 0 && (
        <p style={{
          fontFamily: mono, fontSize: 11, color: C.inkGhost, margin: "10px 0 0", lineHeight: 1.9,
        }}>
          {slots.map((s, i) => (
            <span key={s.id}>
              {i > 0 && " · "}
              <button
                onClick={() => { setOpen(open === s.id ? null : s.id); setError(null); }}
                style={{
                  font: "inherit", color: "inherit", background: "none", border: "none",
                  padding: 0, cursor: "pointer",
                  borderBottom: `1px dotted ${C.ruleStrong}`,
                }}
              >{s.item.label}</button>
            </span>
          ))}
        </p>
      )}

      {slot && (
        <div style={{
          background: C.cardQuiet, borderRadius: 12, border: `1px solid ${C.rule}`,
          padding: "12px 14px", marginTop: 10,
        }}>
          <div style={{ fontSize: 13.5, lineHeight: 1.45, color: C.ink }}>{slot.item.label}</div>

          {/* The one link out of the sheet, above the detail rather than under
              it. A detail runs to a core, four tiers and their tables, and a
              link at the foot of that is a link nobody finds — the cueing is
              the thing you want *before* you start, not after scrolling past
              the sets. It opens the movements at this variant; it records
              nothing and it is not the done button. */}
          {onOpenWorkout && variantOf(slot.item.label) !== null && (
            <button
              onClick={() => onOpenWorkout(variantOf(slot.item.label)!)}
              style={{
                display: "block", fontFamily: mono, fontSize: 11, color: C.inkSoft, letterSpacing: 0.5,
                background: "none", border: "none", padding: 0, marginTop: 8, cursor: "pointer",
                borderBottom: `1px dotted ${C.ruleStrong}`,
              }}
            >the movements and cues for Variant {variantOf(slot.item.label)} ›</button>
          )}

          {/* The detail, when there is one. Rendered, not printed: a detail is
              a core table, four tier tables and the lines around them, and
              pre-wrap text showed him the asterisks and pipes instead of the
              tables. The same renderer the docs and the routine lines use.
              When there is no detail nothing is said about its absence — an
              empty state here would read as a gap to fill, and this is a
              footer, not a form. */}
          {slot.item.detail && (
            <div style={{ marginTop: 8 }}><Markdown text={slot.item.detail} /></div>
          )}

          {error && <p style={{ color: C.alert, fontSize: 12.5, margin: "10px 0 0" }}>{error}</p>}

          {/* One control. There is no skip, no snooze, no "not today" — a
              missed occurrence does not exist, because there is no occurrence
              until one is taken. */}
          <div style={{ display: "flex", gap: 16, alignItems: "center", marginTop: 12 }}>
            <button
              onClick={() => done(slot.id)}
              disabled={busy}
              style={{
                fontFamily: mono, fontSize: 11, letterSpacing: 1,
                background: C.ink, color: C.ground, border: "none", borderRadius: 8,
                padding: "8px 16px", cursor: busy ? "default" : "pointer", opacity: busy ? 0.5 : 1,
              }}
            >DONE</button>
            <button
              onClick={() => { setOpen(null); setError(null); }}
              style={{
                fontFamily: mono, fontSize: 11, color: C.inkFaint,
                background: "none", border: "none", padding: 0, cursor: "pointer",
              }}
            >close</button>
          </div>
        </div>
      )}
    </>
  );
}
